import i18n, { type ResourceLanguage } from "i18next"
import { initReactI18next } from "react-i18next"

import en from "../lang/en.json"
import vi from "../lang/vi.json"

type I18nResources = Record<string, ResourceLanguage>

interface InitializeI18nOptions {
  resources?: I18nResources
  lng?: string
  fallbackLng?: string
}

const sharedResources: I18nResources = {
  en,
  vi,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}

function mergeResourceLanguage(
  base: ResourceLanguage = {},
  override: ResourceLanguage = {}
): ResourceLanguage {
  const merged: Record<string, unknown> = { ...base }

  for (const [key, value] of Object.entries(override)) {
    const currentValue = merged[key]
    merged[key] =
      isRecord(currentValue) && isRecord(value)
        ? mergeResourceLanguage(
            currentValue as ResourceLanguage,
            value as ResourceLanguage
          )
        : value
  }

  return merged as ResourceLanguage
}

function mergeResources(resources: I18nResources = {}): I18nResources {
  const languages = new Set([
    ...Object.keys(sharedResources),
    ...Object.keys(resources),
  ])

  return Array.from(languages).reduce<I18nResources>((acc, language) => {
    acc[language] = mergeResourceLanguage(
      sharedResources[language],
      resources[language]
    )
    return acc
  }, {})
}

function initializeI18n({
  resources,
  lng = "vi",
  fallbackLng = "vi",
}: InitializeI18nOptions = {}) {
  const mergedResources = mergeResources(resources)

  if (i18n.isInitialized) {
    for (const [language, languageResources] of Object.entries(
      mergedResources
    )) {
      for (const [namespace, namespaceResources] of Object.entries(
        languageResources
      )) {
        i18n.addResourceBundle(
          language,
          namespace,
          namespaceResources,
          true,
          true
        )
      }
    }

    void i18n.changeLanguage(lng)
    return i18n
  }

  i18n.use(initReactI18next).init({
    resources: mergedResources,
    lng,
    fallbackLng,
    interpolation: {
      escapeValue: false,
    },
  })

  return i18n
}

initializeI18n()

export { i18n, initializeI18n, mergeResources }
